/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useEffect } from 'react';
import { ContainerInventoryService } from "../../../../../service/MediaContainerServices";
import { mediaReconciliationService } from "../../../../../service/mediaReconciliationServices";
import { PhyReconAdjustmentReason } from "../StartReconciliation.types";
import { useTranslation } from "react-i18next";


interface ReconcileInventoryProps {
  onBack: () => void;
  batchId: string;
  lotsWithNegativeVariance: {
    lot_id: string;
    lot: string;
    system_quantity: number;
    physical_quantity: number;
    difference_quantity: number;
    variance: number;
  }[];
  totalVariance: number;
  onConfirm: (payload: any[]) => void;
}

const ReconcileInventory: React.FC<ReconcileInventoryProps> = ({
  onBack,
  batchId,
  lotsWithNegativeVariance,
  totalVariance,
  onConfirm,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reasonErrors, setReasonErrors] = useState<Record<string, string>>({});
  const [selectionErrors, setSelectionErrors] = useState<Record<string, string>>({});
  const [adjustmentReasons, setAdjustmentReasons] = useState<PhyReconAdjustmentReason[]>([]);
  const { t } = useTranslation();
  
  const fetchReasons = async () => {
    try {
      const response =
        await mediaReconciliationService.getReconMediaAdjustmentReasons(
          "CONTAINER"
        );

      if (response?.statusCode === 200) {
        setAdjustmentReasons(response.data);
      }
    } catch (err) {
      console.error("Adjustment reasons error", err);
    }
  };


  useEffect(() => {
    const fetchContainers = async () => {
      try {
        const responses = await Promise.all(
          lotsWithNegativeVariance.map(async (lot) => {
            const res =
              await ContainerInventoryService.getContainerLotContainerDetails(
                lot.lot_id,
                batchId
              );
            return (res.data || []).map((container: any) => ({
              lot: lot.lot,
              containerId: container.serial_number,
              container_id: container.container_id,
              status: container.operational_status,
              systemQty: 1,
              physicalCount: 1,
              reason: "",
              remarks: "",
            }));
          })
        );
        setItems(responses.flat());
      } catch (err) {
        console.error(err);
      }
    };
    fetchContainers();
  }, [lotsWithNegativeVariance, batchId]);


  useEffect(() => {
    fetchReasons();
  }, []);


  const toggleSelection = (item: any) => {
    const lotVariance = lotsWithNegativeVariance.find(l => l.lot === item.lot);
    if (!lotVariance) return;

    const currentLotSelections = items.filter(i => i.lot === item.lot && selectedIds.includes(i.containerId)).length;
    const isSelected = selectedIds.includes(item.containerId);

    if (!isSelected && currentLotSelections >= lotVariance.variance) {
      alert(`Maximum selection of ${lotVariance.variance} containers reached for Lot ${item.lot}.`);
      return;
    }
    setSelectionErrors(prev => {
      const next = { ...prev };
      delete next[item.lot];
      return next;
    });
    setSelectedIds(prev => 
      prev.includes(item.containerId) 
        ? prev.filter(id => id !== item.containerId) 
        : [...prev, item.containerId]
    );
  };


  const handleReasonChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index].reason = value;
    setItems(updatedItems);
  };

  const handleRemarksChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index].remarks = value;
    setItems(updatedItems);
  };

  
  const handleSubmit = () => {
    for (const lotInfo of lotsWithNegativeVariance) {
      const selectedCount = items.filter(
              i =>
                i.lot === lotInfo.lot &&
                selectedIds.includes(i.containerId)
            ).length;
            if (selectedCount !== lotInfo.variance) {
        setSelectionErrors({
          [lotInfo.lot]:
            `Please select exactly ${lotInfo.variance} containers`
        });

        return;
      }
      const errors: Record<string, string> = {};
      const selectedItemsMissingReason = items.filter(
        i =>
          i.lot === lotInfo.lot &&
          selectedIds.includes(i.containerId) &&
          !i.reason
      );
      selectedItemsMissingReason.forEach(item => {
        errors[item.containerId] = "Please select reason";
      });

      if (Object.keys(errors).length > 0) {
        setReasonErrors(errors);
        return;
      }
      setReasonErrors({});
    }
    const payload = lotsWithNegativeVariance.map((lotInfo) => {
      const selectedContainers = items
        .filter(
          i =>
            i.lot === lotInfo.lot &&
            selectedIds.includes(i.containerId)
        )
        .map(i => ({
          container_id: i.container_id,
          adjustment_reason_id: Number(i.reason),
          remarks: i.remarks || ""
        }));

    return {
      lot_id: Number(lotInfo.lot_id),
      system_quantity: lotInfo.system_quantity,
      physical_quantity: lotInfo.physical_quantity,
      difference_quantity: lotInfo.difference_quantity,
      containers: selectedContainers
    };
  });
    onConfirm(payload);
  };



  return (
    <div className="media media-prep-scope">
    <div className="flex flex-col h-full bg-[#F8FAFC]">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all">
            <span className="material-symbols-outlined text-xl">{t("mediaReconcile.container.arrow_back")} </span>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase">{t("mediaReconcile.container.header")}</h1>
              <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-black uppercase rounded-full border border-orange-200">
                {t("mediaReconcile.container.identifiedUser")}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg font-black text-[10px] uppercase tracking-widest hover:bg-slate-50 shadow-sm transition-all">
            {t("mediaReconcile.container.exportLog")}
          </button>
        </div>
      </div>

      <div className="p-8 pb-32 overflow-y-auto flex-1">
          <div className="space-y-6 animate-in slide-in-from-right duration-500">
             <div className="bg-orange-50 border border-orange-200 rounded-xl p-2 flex gap-4 items-centre shadow-sm">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm text-orange-600 border border-orange-100 shrink-0">
                  <span className="material-symbols-outlined text-lg">{t("mediaReconcile.container.feedback")}</span>
                </div>
                <div className="flex-1">
                   <h3 className="text-[13px] font-black text-slate-800 uppercase tracking-tight leading-tight">{t("mediaReconcile.container.negVariance")}</h3>
                   <p className="text-[12px] text-slate-600 font-medium mt-0.5">{t("mediaReconcile.container.infoTextcontainer")}</p>
                </div>
                {selectedIds.length > 0 && (
                  <div className="flex bg-white p-1.5 rounded-lg border border-orange-200 shadow-sm gap-3 items-center animate-in zoom-in-95 duration-200 shrink-0">
                    <div className="pl-1 border-r border-slate-100 pr-3">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">{t("mediaReconcile.container.BulkAction")}</p>
                       <p className="text-[10px] font-black text-orange-600 leading-none">{selectedIds.length} {t("mediaReconcile.container.Selected")}</p>
                    </div>
                    <button 
                      onClick={() => setSelectedIds([])}
                      className="text-slate-400 hover:text-slate-600 transition-colors px-1"
                    >
                      <span className="material-symbols-outlined text-lg">{t("mediaReconcile.container.close")}</span>
                    </button>
                  </div>
                )}
             </div>

             <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="bg-[#2D3E50] px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-white text-lg">{t("mediaReconcile.container.list_alt")}</span>
                    <h3 className="text-[11px] font-black text-white tracking-widest uppercase">{t("mediaReconcile.container.identifiedContainers")}</h3>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <span className="text-[9px] font-black text-white/70 uppercase gap-1.5">{t("mediaReconcile.container.Identified")}</span>
                    </div>
                    <div className="flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                       <span className="text-[9px] font-black text-white/70 uppercase gap-1.5">{t("mediaReconcile.container.LimitReached")}</span>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                   {/* Grouped display for lots with negative variance */}
                   {lotsWithNegativeVariance.map(lotInfo => {
                     const lotAllItems = items.filter(i => i.lot === lotInfo.lot);
                     const selectedInLot = lotAllItems.filter(i => selectedIds.includes(i.containerId));
                     const isLimitReached = selectedInLot.length >= lotInfo.variance;
                     
                     return (
                      <div key={lotInfo.lot} className="mb-3 last:mb-0">
                         <div className="flex items-center justify-between mb-3 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                            <div className="flex flex-col gap-1">
                               <div className="flex items-center gap-2">
                                  <span className="px-1.5 py-0.5 bg-white rounded text-[10px] font-black text-slate-400 uppercase tracking-widest border border-slate-200">Lot Num</span>
                                  <span className="text-[12.5px] font-black text-slate-800">{lotInfo.lot}</span>
                               </div>
                               <div className="relative">
                                  <div className="flex -space-x-1 absolute left-0 top-4">
                                     {[...Array(lotInfo.variance)].map((_, i) => (
                                       <div key={i} className={`w-2.5 h-2.5 rounded-full border border-white ${i < selectedInLot.length ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                     ))}
                                  </div>
                                  <p className="text-[10px] font-bold text-slate-500 pl-[2px]">Required:
                                    <span className="text-red-500 font-black">{lotInfo.variance}</span>
                                    {" "}containers
                                  </p>
                                  <span>
                                    {selectionErrors[lotInfo.lot] && (<p className="text-red-500 text-[11px] mt-0.35 font-semibold">{selectionErrors[lotInfo.lot]}</p>)}
                                  </span>
                               </div>
                            </div>
                            <div className="flex flex-col items-end">
                               <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border ${
                                 isLimitReached ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-orange-50 border-orange-100 text-orange-600'
                               }`}>
                                  {selectedInLot.length} / {lotInfo.variance} Identified
                               </span>
                            </div>
                         </div>
                         <div className="grid grid-cols-1 gap-2">
                            {lotAllItems.map((item, idx) => {
                               const isSelected = selectedIds.includes(item.containerId);
                               const isDisabled = !isSelected && isLimitReached;
                               
                               return (
                               <div key={idx} className={`flex items-center justify-between py-1.5 px-3 rounded-xl border transition-all ${
                                 isSelected 
                                   ? 'bg-emerald-50/20 border-emerald-200' 
                                   : isDisabled 
                                     ? 'bg-slate-50/50 border-slate-100 opacity-60' 
                                     : 'bg-white border-slate-100 hover:border-slate-200'
                               }`}>
                                  <div className="flex items-center gap-4">
                                     <div className="relative group/cb">
                                        <input 
                                          type="checkbox" 
                                          disabled={isDisabled}
                                          checked={isSelected}
                                          onChange={() => toggleSelection(item)}
                                          className={`w-5 h-5 rounded border text-emerald-600 focus:ring-emerald-500 transition-all ${
                                            isDisabled ? 'cursor-not-allowed border-slate-200 bg-slate-50' : 'cursor-pointer border-slate-200'
                                          }`}
                                        />
                                     </div>
                                     <div className={`w-9 h-9 rounded-xl flex items-center justify-center border shrink-0 ${
                                       isSelected ? 'bg-emerald-100 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-100'
                                     }`}>
                                        <span className="material-symbols-outlined text-[17px]">
                                          {isSelected ? 'verified' : 'inventory_2'}
                                        </span>
                                     </div>
                                     <div className="min-w-[100px] relative top-2">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">Container ID</p>
                                        <p className={`text-[11px] font-bold ${isSelected ? 'text-emerald-700' : 'text-slate-700'}`}>{item.containerId}</p>
                                     </div>
                                     <div className="w-px h-6 bg-slate-100 mx-1"></div>
                                     <div className="relative top-2">
                                        <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0">Status</p>
                                        <div className="flex items-center gap-2">
                                           <span
                                              className={`w-1.5 h-1.5 rounded-full relative -top-1 ${
                                                item.status === "EMPTY_AVAILABLE"
                                                  ? "bg-emerald-500"
                                                  : item.status === "media_dispensed"
                                                  ? "bg-blue-500"
                                                  : item.status === "final_quality_rejected"
                                                  ? "bg-red-500"
                                                  : "bg-slate-400"
                                              }`}
                                            ></span>
                                           <p className="text-[10px] font-bold text-slate-500 relative top-0.5">
                                              {item.status}
                                           </p>
                                        </div>
                                     </div>
                                  </div>
                                  
                                  {isSelected && (
                                    <div className="flex items-start gap-3 animate-in fade-in duration-300">
                                       <div className="w-48">
                                          <p className="text-[8px] font-black text-emerald-600 uppercase tracking-widest mb-1 ml-1 leading-none">
                                             {t("mediaReconcile.container.Reason")} <span className="text-red-500">*</span>
                                          </p>
                                          <select 
                                             value={item.reason}
                                             onChange={(e) => {
                                               const realIndex = items.findIndex(i => i.containerId === item.containerId && i.lot === item.lot);
                                               handleReasonChange(realIndex, e.target.value);
                                               setReasonErrors(prev => {const next = { ...prev };delete next[item.containerId];return next;});
                                             }}
                                             className={`w-full py-1.5 px-3 rounded-lg text-[10px] font-bold focus:ring-1 focus:ring-emerald-500 outline-none border transition-all h-8 ${
                                               !item.reason ? 'bg-red-50/30 border-red-200 text-red-600' : 'bg-white border-slate-200 text-slate-700'
                                             }`}
                                           >
                                             <option value="">Required...</option>
                                             {adjustmentReasons.map((r) => (
                                                <option
                                                  key={r.id}
                                                  value={r.id}
                                                >
                                                  {r.reason_name}
                                                </option>
                                              ))}
                                           </select>
                                           {reasonErrors[item.containerId] && (<p className="text-red-500 text-[10px] mt-1 font-semibold">{reasonErrors[item.containerId]}</p>)}
                                       </div>
                                       <div className="w-56">
                                          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1 ml-1 leading-none">Optional Remarks</p>
                                          <input 
                                             type="text"
                                             placeholder="Context..."
                                             value={item.remarks || ''}
                                             onChange={(e) => {
                                               const realIndex = items.findIndex(i => i.containerId === item.containerId && i.lot === item.lot);
                                               handleRemarksChange(realIndex, e.target.value);
                                             }}
                                             className="w-full py-1.5 px-3 bg-white border border-slate-200 rounded-lg text-[10px] font-bold text-slate-700 focus:ring-1 focus:ring-emerald-500 h-8 outline-none"
                                          />
                                       </div>
                                    </div>
                                  )}
                               </div>
                               );
                            })}
                         </div>
                      </div>
                     );
                   })}
                </div>
             </div>
          </div>

        {/* Regulatory Box */}
        <div className="bg-[#F8FAFC] border-l-4 border-[#2D3E50] p-6 rounded-r-xl flex items-start gap-4 shadow-sm">
          <span className="material-symbols-outlined text-[#2D3E50] mt-0.5">{t("mediaReconcile.container.verified_user")}</span>
          <div>
            <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-1">{t("mediaReconcile.container.regulatoryInfo")}</h4>
            <p className="text-[11px] text-slate-500 leading-relaxed font-medium">
              {t("mediaReconcile.container.info")}
            </p>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <footer className="fixed bottom-0 right-0 left-64 bg-white border-t border-gray-200 p-6 flex items-center justify-between z-40 shadow-[0_-10px_20px_-15px_rgba(0,0,0,0.1)]">
        <div className="flex gap-4">
            <button 
              onClick={onBack}
              className="px-8 py-3 bg-white border border-gray-200 text-slate-600 rounded-lg font-black text-[11px] uppercase tracking-widest hover:bg-gray-50 active:scale-95 transition-all"
            >
              {t("mediaReconcile.container.back")}
            </button>
        </div>

        <div className="flex items-center gap-12">
          <div className="text-right">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t("mediaReconcile.container.totalVariance")}</p>
            <p className={`text-xl font-black ${
              totalVariance < 0
                ? 'text-red-500'
                : 'text-emerald-500'
            }`}>
              {totalVariance > 0 ? '+' : ''}
              {totalVariance.toFixed(2)}
            </p>
          </div>
          <button 
            onClick={handleSubmit}
            className="flex items-center gap-3 px-10 py-3 bg-[#FF7043] text-white rounded-lg font-black text-sm uppercase tracking-widest shadow-lg shadow-orange-500/30 hover:brightness-110 active:scale-95 transition-all"
          >
            <span className="material-symbols-outlined text-lg">
              {t("mediaReconcile.container.verified_user")}
            </span>
            {t("mediaReconcile.container.complete")}
          </button>
        </div>
      </footer>
    </div>        
    </div>
  );
};

export default ReconcileInventory;